import type { IFormField } from "@/types/app";
import { Controller, useWatch } from "react-hook-form";
import type { Control, FieldErrors } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { generateUsernameFromName } from "@/lib/generate-username";

interface Props extends IFormField {
  errors: FieldErrors;
  control: Control<Record<string, unknown>>;
}

const UsernameField = ({
  label,
  name,
  placeholder,
  disabled,
  autoFocus,
  control,
  errors,
  description,
  showGenerateButton,
  generateFromField = "name",
}: Props) => {
  const sourceName = useWatch({
    control,
    name: generateFromField,
  });
  const suggestedUsername = generateUsernameFromName(String(sourceName ?? ""));

  return (
    <div className="flex flex-col gap-2">
      {(label || showGenerateButton) && (
        <div className="flex items-center justify-between gap-2">
          {label && (
            <Label
              htmlFor={name}
              className="text-sm font-medium text-card-foreground"
            >
              {label}
            </Label>
          )}
          {showGenerateButton && (
            <Controller
              control={control}
              name={name}
              render={({ field: { onChange } }) => (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 shrink-0 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                  disabled={disabled || !suggestedUsername}
                  onClick={() => {
                    if (suggestedUsername) onChange(suggestedUsername);
                  }}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  توليد من الاسم
                </Button>
              )}
            />
          )}
        </div>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <Input
            type="text"
            onChange={onChange}
            onBlur={onBlur}
            value={String(value || "")}
            ref={ref}
            placeholder={placeholder || ""}
            disabled={disabled || false}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus || false}
            autoComplete="username"
            id={name}
            className={`${
              errors[name]
                ? "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20"
                : "!border-border !shadow-none focus-visible:ring-0 focus-visible:border-border placeholder:text-placeholder"
            } h-10 !bg-transparent text-right`}
            aria-invalid={errors[name] ? "true" : "false"}
          />
        )}
      />
      {description && (
        <p className="text-right text-xs text-muted-foreground">{description}</p>
      )}
      {errors[name] && (
        <p className="text-sm text-destructive mt-1">
          {errors[name]?.message as string}
        </p>
      )}
    </div>
  );
};

export default UsernameField;
